import { useSignal } from '@preact/signals';
import type { ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';

import type { WeatherDayHourView, WeatherDayView } from '@amumin/api-client';
import { t } from '@amumin/i18n';
import { Button, Dialog, SegmentedControl } from '@amumin/ui';
import { Icon, weatherSymbolUrl } from '@amumin/ui-core';

import { appText, intlLocale, messages } from '../state/session.js';
import styles from './weatherDetail.module.css';

// Weerdetail in de ouderapp (golf 6b; oprichter 2026-09-26: "als ik op weer knop druk, moet ik meer
// informatie krijgen over de dag, zoals regen windkracht gevoelstemperatuur, als ik verder klik moet
// ik verloop van de dag zien, zon regen etc"). Eerst de dag in tegels (regen, wind, gevoel,
// zonkracht, zon op/onder); "Verloop per uur" toont de dag als lijn (temperatuur), staafjes
// (neerslag) en weerbeelden per uur, met "Nu" en de verstreken uren rustig grijs. Alles komt van de
// server (GET /v1/app/weather/today → days); hier geen weerlogica, alleen tonen.

/** Breedste kolom per uur; smaller (tot MIN_COL) als de dag dan in één keer in beeld past. */
const COL_MAX = 44;
const MIN_COL = 30;
const CHART_H = 120;
const RAIN_H = 44;
const RAIN_MAX_MM = 4;

function hhmmNowIn(timeZone: string | undefined): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(new Date());
  } catch {
    return '';
  }
}

export interface WeatherDetailProps {
  open: boolean;
  onClose: () => void;
  days: ReadonlyArray<WeatherDayView>;
  /** Welke dag eerst ('s avonds morgen, zoals de kaart). */
  initialDate: string | null;
  /** Vandaag in de tijdzone van het gezin (verstreken uren grijs, "Nu"). */
  todayKey: string;
  timeZone: string | undefined;
  attribution: string;
  locationLabel: string | null;
}

export function WeatherDetailDialog(props: WeatherDetailProps) {
  const a = appText.value;
  const d = a.weatherDetail;
  const m = messages.value;
  const date = useSignal<string | null>(props.initialDate);
  const view = useSignal<'day' | 'course'>('day');

  useEffect(() => {
    if (props.open) {
      date.value = props.initialDate ?? props.days[0]?.date ?? null;
      view.value = 'day';
    }
  }, [props.open]);

  const day = props.days.find((x) => x.date === date.value) ?? props.days[0] ?? null;
  const isToday = day?.date === props.todayKey;
  const title = isToday || !day ? d.titleToday : d.titleTomorrow;
  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      title={props.locationLabel ? `${title} · ${props.locationLabel}` : title}
      closeLabel={m.design.dialog.close}
      name="weather-detail"
      actions={
        view.value === 'course' ? (
          <Button
            name="weather-course-back"
            variant="ghost"
            icon="chevron-left"
            onPress={() => (view.value = 'day')}
          >
            {d.back}
          </Button>
        ) : day ? (
          <Button
            name="weather-course-open"
            variant="primary"
            icon="clock"
            onPress={() => (view.value = 'course')}
          >
            {d.courseOpen}
          </Button>
        ) : null
      }
    >
      {props.days.length > 1 ? (
        <div class={styles.dayPick}>
          <SegmentedControl<string>
            label={d.titleToday}
            name="weather-day"
            value={day?.date ?? ''}
            onChange={(v) => (date.value = v)}
            options={props.days.map((x) => ({
              value: x.date,
              label: x.date === props.todayKey ? d.today : d.tomorrow,
            }))}
          />
        </div>
      ) : null}
      {!day ? null : view.value === 'day' ? (
        <DaySummary day={day} />
      ) : (
        <DayCourse
          day={day}
          isToday={isToday}
          nowHhmm={isToday ? hhmmNowIn(props.timeZone) : null}
        />
      )}
      {day && !day.complete ? <p class="muted small">{d.partial}</p> : null}
      <p class={styles.credit} data-name="weather-detail-attribution">
        <Icon id="info" size={14} /> {props.attribution}
      </p>
    </Dialog>
  );
}

function useNumber() {
  const fmt = new Intl.NumberFormat(intlLocale.value, { maximumFractionDigits: 1 });
  return (n: number) => fmt.format(n);
}

function Tile({
  name,
  label,
  main,
  children,
  icon,
}: {
  name: string;
  label: string;
  main: string;
  children?: ComponentChildren;
  icon?: ComponentChildren;
}) {
  return (
    <li class={styles.tile} data-name={name}>
      <span class={styles.tileLabel}>{label}</span>
      <span class={styles.tileMain}>
        {icon}
        <span>{main}</span>
      </span>
      {children ? <span class={styles.tileMore}>{children}</span> : null}
    </li>
  );
}

function DaySummary({ day }: { day: WeatherDayView }) {
  const a = appText.value;
  const d = a.weatherDetail;
  const num = useNumber();
  const dayHour = day.hours.find((x) => x.hhmm === '13:00') ?? day.hours[0];
  const rainBits: string[] = [];
  if (day.rain_hours > 0) {
    rainBits.push(
      `${t(d.rainAmount, { mm: num(day.rain_mm) })} ${t(d.rainHours, { count: day.rain_hours })}`,
    );
    if (day.rain_from && day.rain_until) {
      rainBits.push(t(d.rainWhen, { from: day.rain_from, until: day.rain_until }));
    }
  }
  if (day.rain_pct !== null) rainBits.push(t(d.rainPct, { pct: day.rain_pct }));
  return (
    <div data-name="weather-day" data-date={day.date}>
      <div class={styles.hero}>
        <img
          src={weatherSymbolUrl(day.symbol, dayHour ? dayHour.is_day : true)}
          width={72}
          height={72}
          alt={d.symbols[day.symbol]}
        />
        <span>
          <strong class={styles.heroTemp} data-name="weather-day-temp">
            {t(d.tempRange, { min: day.temp_min_c, max: day.temp_max_c })}
          </strong>
          <span class="muted block">{d.symbols[day.symbol]}</span>
        </span>
      </div>
      <ul class={styles.tiles}>
        <Tile
          name="weather-tile-rain"
          label={d.rain}
          main={d.rainChance[day.rain_chance]}
          icon={<img src={weatherSymbolUrl('rain', true)} width={28} height={28} alt="" />}
        >
          {rainBits.join(' · ')}
          {day.rain_pct === null && day.rain_hours > 0 ? (
            <span class="block muted small">{d.rainNoPct}</span>
          ) : null}
        </Tile>
        <Tile
          name="weather-tile-wind"
          label={d.wind}
          main={`${t(d.windForce, { bft: day.wind_bft })} · ${d.bft[day.wind_bft] ?? ''}`}
          icon={<WindArrow dir={day.wind_dir} />}
        >
          {day.wind_dir ? `${t(d.windFrom, { dir: d.dirs[day.wind_dir] })} · ` : ''}
          {t(d.windSpeed, { kmh: Math.round(day.wind_ms * 3.6) })}
        </Tile>
        <Tile
          name="weather-tile-feels"
          label={d.feels}
          main={t(d.feelsRange, { min: day.feels_min_c, max: day.feels_max_c })}
          icon={<Icon id="hand" size={24} />}
        >
          {t(d.feelsDay, { value: day.feels_day_c })}
        </Tile>
        {day.uv_max !== null && day.uv_level ? (
          <Tile
            name="weather-tile-uv"
            label={d.uv}
            main={t(d.uvValue, { value: day.uv_max, level: d.uvLevels[day.uv_level] })}
            icon={<Icon id="sun" size={24} />}
          >
            {day.uv_kind === 'clear_sky' ? d.uvClearSky : null}
          </Tile>
        ) : null}
        {day.sunrise || day.sunset ? (
          <Tile
            name="weather-tile-sun"
            label={d.sun}
            main={day.sunrise ? t(d.sunrise, { time: day.sunrise }) : ''}
            icon={<Icon id="sunrise" size={24} />}
          >
            <Icon id="sunset" size={16} /> {day.sunset ? t(d.sunset, { time: day.sunset }) : ''}
          </Tile>
        ) : null}
      </ul>
    </div>
  );
}

/** Pijltje in de richting waar de wind naartoe waait (van het noorden = pijl omlaag). */
function WindArrow({ dir }: { dir: WeatherDayView['wind_dir'] }) {
  if (!dir) return <Icon id="refresh-cw" size={24} />;
  const deg = { N: 0, NE: 45, E: 90, SE: 135, S: 180, SW: 225, W: 270, NW: 315 }[dir];
  return (
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 24"
      aria-hidden="true"
      class={styles.windArrow}
      style={{ transform: `rotate(${deg}deg)` }}
    >
      <path d="M12 3v16M6 13l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2.2" />
    </svg>
  );
}

function DayCourse({
  day,
  isToday,
  nowHhmm,
}: {
  day: WeatherDayView;
  isToday: boolean;
  nowHhmm: string | null;
}) {
  const a = appText.value;
  const d = a.weatherDetail;
  const num = useNumber();
  const scroller = useRef<HTMLDivElement>(null);
  // Review golf 6b founder-ux r1: "het verloop van de dag" in één oogopslag. Net als op de tablet
  // van 06:00 tot en met 23:00 (de nacht alleen als je hem nu meemaakt), en de kolommen zo smal als
  // nodig om alles in het venster te krijgen; past het niet, dan een vervaging als scrollhint.
  const nightNow = isToday && nowHhmm !== null && nowHhmm < '06:00';
  const hours = nightNow ? day.hours : day.hours.filter((x) => x.hhmm >= '06:00');
  const boxW = useSignal(0);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const measure = () => (boxW.value = el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const COL =
    boxW.value > 0 && hours.length > 0
      ? Math.max(MIN_COL, Math.min(COL_MAX, Math.floor(boxW.value / hours.length)))
      : COL_MAX;
  const overflow = boxW.value > 0 && hours.length * COL > boxW.value + 1;
  const temps = hours.map((x) => x.temp_c);
  const tMin = Math.min(...temps);
  const tMax = Math.max(...temps);
  const span = Math.max(4, tMax - tMin);
  const y = (tc: number) => 16 + (1 - (tc - tMin) / span) * (CHART_H - 40);
  const width = hours.length * COL;
  const nowIndex =
    isToday && nowHhmm
      ? hours.findIndex((x, i) => x.hhmm <= nowHhmm && (hours[i + 1]?.hhmm ?? '99') > nowHhmm)
      : -1;
  const points = hours.map((x, i) => `${i * COL + COL / 2},${y(x.temp_c).toFixed(1)}`).join(' ');

  // Meteen bij "nu" (of 07:00) beginnen: de nacht staat links buiten beeld. Na de opbouw van het
  // venster (anders is de breedte nog 0 en blijft hij op de nacht staan).
  useEffect(() => {
    const el = scroller.current;
    if (!el || !overflow) return;
    const start =
      nowIndex >= 0
        ? nowIndex
        : Math.max(
            0,
            hours.findIndex((x) => x.hhmm >= '07:00'),
          );
    const rtl = document.documentElement.dir === 'rtl';
    const offset = Math.max(0, start * COL - COL);
    el.scrollLeft = rtl ? -offset : offset;
  }, [day.date, COL, overflow]);

  const past = (x: WeatherDayHourView, i: number) => nowIndex >= 0 && i < nowIndex;

  return (
    <div data-name="weather-course" data-date={day.date}>
      <h3 class={styles.courseTitle}>{d.course}</h3>
      <div class={styles.legend} aria-hidden="true">
        <span class={styles.legendTemp}>{d.courseTemp}</span>
        <span class={styles.legendRain}>{d.courseRain}</span>
      </div>
      <div
        class={overflow ? `${styles.scroller} ${styles.scrollHint}` : styles.scroller}
        ref={scroller}
        tabIndex={0}
        aria-label={d.course}
      >
        <div class={styles.chart} style={{ width: `${width}px` }} dir="ltr">
          <div class={styles.symbolRow}>
            {hours.map((x, i) => (
              <span
                key={x.at_utc}
                class={past(x, i) ? `${styles.cell} ${styles.past}` : styles.cell}
                style={{ width: `${COL}px` }}
              >
                <img
                  src={weatherSymbolUrl(x.symbol, x.is_day)}
                  width={28}
                  height={28}
                  alt=""
                  loading="lazy"
                />
              </span>
            ))}
          </div>
          <svg
            class={styles.svg}
            width={width}
            height={CHART_H + RAIN_H}
            viewBox={`0 0 ${width} ${CHART_H + RAIN_H}`}
            aria-hidden="true"
          >
            {nowIndex >= 0 ? (
              <rect
                class={styles.pastShade}
                x={0}
                y={0}
                width={nowIndex * COL}
                height={CHART_H + RAIN_H}
              />
            ) : null}
            <polyline class={styles.tempLine} points={points} />
            {hours.map((x, i) => {
              const cx = i * COL + COL / 2;
              const bar = Math.min(1, x.precip_mm / RAIN_MAX_MM) * (RAIN_H - 8);
              return (
                <g key={x.at_utc}>
                  <circle class={styles.tempDot} cx={cx} cy={y(x.temp_c)} r={3.2} />
                  <text class={styles.tempText} x={cx} y={y(x.temp_c) - 9} text-anchor="middle">
                    {x.temp_c}°
                  </text>
                  {x.precip_mm >= 0.1 ? (
                    <>
                      <rect
                        class={styles.rainBar}
                        x={cx - 9}
                        y={CHART_H + RAIN_H - bar}
                        width={18}
                        height={Math.max(2, bar)}
                        rx={3}
                      />
                      <text
                        class={styles.rainText}
                        x={cx}
                        y={CHART_H + RAIN_H - bar - 4}
                        text-anchor="middle"
                      >
                        {num(x.precip_mm)}
                      </text>
                    </>
                  ) : null}
                </g>
              );
            })}
            {nowIndex >= 0 ? (
              <line
                class={styles.nowLine}
                x1={nowIndex * COL + 2}
                x2={nowIndex * COL + 2}
                y1={0}
                y2={CHART_H + RAIN_H}
              />
            ) : null}
          </svg>
          <div class={styles.hourRow}>
            {hours.map((x, i) => (
              <span
                key={x.at_utc}
                class={
                  i === nowIndex
                    ? `${styles.cell} ${styles.nowCell}`
                    : past(x, i)
                      ? `${styles.cell} ${styles.past}`
                      : styles.cell
                }
                style={{ width: `${COL}px` }}
              >
                {i === nowIndex ? d.now : x.hhmm.slice(0, 2)}
              </span>
            ))}
          </div>
          <div class={styles.windRow}>
            {hours.map((x, i) => (
              <span
                key={x.at_utc}
                class={past(x, i) ? `${styles.cell} ${styles.past}` : styles.cell}
                style={{ width: `${COL}px` }}
              >
                {x.wind_bft}
              </span>
            ))}
          </div>
        </div>
      </div>
      {/* Fixer golf 6b r2: de onderste rij getallen is de wind; op de telefoon stond die uitleg los
          boven de grafiek, dus nu direct onder de rij. */}
      <p class={styles.windCaption} aria-hidden="true" data-name="weather-wind-caption">
        <span class={styles.windCaptionArrow}>↑</span> {d.wind} (Bft)
      </p>
      {/* Voor schermlezers: hetzelfde als lijst. */}
      <ul class="fb-sr-only">
        {hours.map((x) => (
          <li key={x.at_utc}>
            {t(d.courseHour, {
              time: x.hhmm,
              temp: x.temp_c,
              rain: num(x.precip_mm),
              bft: x.wind_bft,
            })}
            , {d.symbols[x.symbol]}
          </li>
        ))}
      </ul>
    </div>
  );
}
